<template>
  <RfDialog
    ref="modalRef"
    title="Clone Prompt"
    width="580"
    confirmText="Clone"
    :disabled="
      cloneAsVariation
        ? !targetExperimentPath?.value && 'Target experiment is required'
        : (isPlacementComputed &&
            cloneIntoCurrApp &&
            !cloneItemZone?.value &&
            'Zone is required') ||
          (!cloneAppTarget?.value && 'App is required') ||
          (showEngagementCategoryField &&
            !cloneEngagementCategory &&
            'Engagement category is required')
    "
    @confirm="
      cloneAsVariation
        ? emit('cloneAsVariation', { targetPathId: targetExperimentPath?.value })
        : emit('clone', {
            targetAppId: cloneAppTarget.value,
            targetAppName: cloneAppTarget.title,
            pathGroupId: cloneItemZone?.value,
            sequenceId: cloneItemSequence?.value,
            sequenceName: cloneItemSequence?.title,
            defaultCompany:
              !cloneCompanyTarget?.value || props.currApp.company_id === cloneCompanyTarget?.value,
            engagementCategory: cloneItemSequence?.value ? null : cloneEngagementCategory,
          })
    "
    @close="emit('update:item', null)"
  >
    <div class="flex flex-col gap-4">
      <div>
        This will create a copy of your prompt with the current design and details. Custom Goals,
        Triggers, Actions, and Experiments will also be cloned. You can choose to clone this into
        another Zone or Guide, within this app.
      </div>
      <RfSelect
        v-if="isSuperAdmin"
        v-model="cloneCompanyTarget"
        :items="companies"
        :loading="companiesLoading"
        search="Search company..."
        itemValue="title"
        variant="outlined"
        returnObject
        sameWidth
        title="Company"
      />
      <template v-if="showAppPicker">
        <RfSelect
          v-model="cloneAppTarget"
          :items="apps"
          search="Search app..."
          itemValue="title"
          variant="outlined"
          returnObject
          sameWidth
          title="App"
        />
        <RfAlert v-if="!cloneIntoCurrApp" type="error">
          <template v-if="isPlacementComputed">
            This requires a similar Zone of the same identifier and type in the target app.
          </template>
          <template v-else> This will be placed into a new Zone in the target app. </template>
          Custom Goals, Actions, Triggers, Segments, and certain other prompt properties will not be
          cloned.
        </RfAlert>
      </template>

      <RfSelect
        v-if="isPlacementComputed && cloneIntoCurrApp"
        v-model="cloneItemZone"
        :items="filteredPathGroupItems"
        :loading="pathGroups === null"
        itemValue="title"
        variant="outlined"
        returnObject
        sameWidth
        title="Zone"
      />
      <RfSelect
        v-if="cloneIntoCurrApp"
        v-model="cloneItemSequence"
        :items="filteredSequences"
        :loading="sequences === null"
        itemValue="title"
        variant="outlined"
        returnObject
        sameWidth
        title="Guide"
      />
      <div v-if="showEngagementCategoryField" class="flex flex-col gap-2">
        <div class="text-sm text-gray-600">
          {{ ENGAGEMENT_CATEGORY_DESCRIPTION.prompt }}
        </div>
        <RfSelect
          v-model="cloneEngagementCategory"
          title="Engagement Category"
          placeholder="Select an engagement category"
          :items="engagementCategories"
          variant="outlined"
          :itemDisabled="item => item.disabled"
          :itemDisabledMessage="item => item.disabledMessage"
          sameWidth
          itemValue="label"
        >
          <template #selected="{ props: tag }">
            <RfColorTag
              :title="tag.label"
              pill
              disabled
              :backgroundColor="getCategoryColor(tag.value)"
            />
          </template>
          <template #item="{ props: tag }">
            <div class="flex flex-col items-start">
              <RfColorTag
                :title="tag.label"
                pill
                disabled
                :backgroundColor="getCategoryColor(tag.value)"
              />
              <span class="text-grey-2 ml-2 text-sm">{{ tag.description }}</span>
            </div>
          </template>
        </RfSelect>
      </div>
      <div
        v-if="cloneIntoCurrApp && pathsWithExperiments.length > 0 && !props.item.experiment"
        class="flex flex-col gap-2 pt-2"
      >
        <RfCheckbox v-model="cloneAsVariation">Clone as experiment variation</RfCheckbox>
        <template v-if="cloneAsVariation">
          <RfSelect
            v-model="targetExperimentPath"
            :items="pathsWithExperiments"
            itemValue="title"
            variant="outlined"
            returnObject
            sameWidth
            title="Target experiment (prompt)"
          />
          <RfAlert type="info">
            The design and content of this prompt will be added as a new variation in the selected
            experiment. Traffic will be split equally across all variations. The source prompt
            remains unchanged.
          </RfAlert>
        </template>
      </div>
    </div>
  </RfDialog>
</template>

<script setup>
import ApiApps from "@/apis/ApiApps";
import ApiCompanies from "@/apis/ApiCompanies";
import ApiPathGroups from "@/apis/ApiPathGroups";
import ApiSequences from "@/apis/ApiSequences";
import RfCheckbox from "@/components/inputs/RfCheckbox.vue";
import RfSelect from "@/components/inputs/RfSelect.vue";
import RfDialog from "@/components/modals/RfDialog.vue";
import { useAllPromptsStore } from "@/pinia/allPromptsStore";
import { catchable } from "@/pinia/piniaUtils";
import { isPlacement } from "@/utils/prompts/promptHelpers";
import { isRunning, isInvalidSequenceTypeForPromptDevice } from "@/utils/guideHelpers";
import {
  ENGAGEMENT_CATEGORY_DESCRIPTION,
  getCategoryColor,
} from "@/utils/constants/EngagementCategoryConstants";
import { useEngagementCategories } from "@/utils/composables/useEngagementCategories";
import { computed, onMounted, ref, watch } from "vue";
import RfAlert from "@/components/alerts/RfAlert.vue";
import RfColorTag from "@/components/tags/RfColorTag.vue";

const emit = defineEmits(["clone", "cloneAsVariation", "update:item"]);
const props = defineProps({
  item: { type: Object, default: null },
  isSuperAdmin: { type: Boolean, default: false },
  isCompanyAdmin: { type: Boolean, default: false },
  currApp: { type: Object, default: () => ({}) },
  currUser: { type: Object, default: () => ({}) },
});

const apps = ref([]);
const companies = ref([]);
const companiesLoading = ref(false);
const pathGroups = ref(null);
const sequences = ref(null);
const modalRef = ref();
const cloneItemZone = ref();
const cloneItemSequence = ref();
const cloneAppTarget = ref({});
const cloneCompanyTarget = ref({});
const cloneEngagementCategory = ref(null);
const cloneAsVariation = ref(false);
const targetExperimentPath = ref(null);
const pathsWithExperiments = ref([]);
const allPromptsStore = useAllPromptsStore();

const isPlacementComputed = computed(() => isPlacement(props.item.path_type));
const cloneIntoCurrApp = computed(() => props.currApp.id === cloneAppTarget.value.value);
const showAppPicker = computed(
  () =>
    apps.value.length >
    (!cloneCompanyTarget.value?.value ||
    cloneCompanyTarget.value?.value === props.currApp.company_id
      ? 1
      : 0),
);
const filteredPathGroupItems = computed(() => {
  if (!pathGroups.value) return [{ title: "Loading..." }];
  return pathGroups.value
    .filter(
      ({ path_type, device_type }) =>
        path_type === props.item.path_type && device_type === props.item.device_type,
    )
    .map(({ name, id }) => ({ title: name, value: id }));
});
const filteredSequences = computed(() => {
  if (!sequences.value) return [{ title: "Loading..." }];
  const filtered = sequences.value.filter(
    seq => !isRunning(seq) && !isInvalidSequenceTypeForPromptDevice(seq, props.item),
  );
  return [
    { title: "(none)", value: null },
    ...filtered.map(({ name, id }) => ({ title: name, value: id })),
  ];
});
const { engagementCategoriesEnabled, engagementCategoriesWithRestrictions: engagementCategories } =
  useEngagementCategories();
const showEngagementCategoryField = computed(() => {
  const isStandalone = !cloneItemSequence.value?.value;
  return engagementCategoriesEnabled.value && isStandalone;
});

const loadPathsWithExperiments = async () => {
  if (!cloneIntoCurrApp.value) return;
  if (!allPromptsStore.prompts.length) await allPromptsStore.getPrompts(props.currApp.id);
  if (!cloneIntoCurrApp.value) return;
  pathsWithExperiments.value = allPromptsStore.prompts
    .filter(
      p =>
        p.experiment &&
        !p.experiment.is_active &&
        !p.experiment.is_completed &&
        p.id !== props.item.id &&
        p.path_type === props.item.path_type &&
        p.device_type === props.item.device_type,
    )
    .map(p => ({ title: `${p.name} (${p.experiment.name})`, value: p.id }));
};

let lastFetchedCompanyId;
const getApps = companyId => {
  lastFetchedCompanyId = companyId;
  return catchable({
    t: async () => {
      const appsData = await ApiApps.getApps(companyId);
      apps.value = (
        props.isSuperAdmin || props.isCompanyAdmin
          ? appsData
          : appsData.filter(app => {
              const userApp = props.currUser.apps.find(userApp => userApp.id === app.id);
              return userApp && userApp.role !== "read_only";
            })
      ).map(el => ({ title: el.name, value: el.id }));
      if (showAppPicker.value)
        cloneAppTarget.value =
          apps.value.find(el => el.value === props.currApp?.id) || apps.value[0];
      else if (
        (!props.isSuperAdmin && !props.isCompanyAdmin) ||
        (apps.value.length === 1 && apps.value[0].value === props.currApp.id)
      )
        cloneAppTarget.value = { value: props.currApp.id, title: props.currApp.name };
      else cloneAppTarget.value = {};
    },
    loadable: true,
  });
};
let showInProgress = false;

const show = async () => {
  showInProgress = true;
  modalRef.value?.open();
  cloneAsVariation.value = false;
  targetExperimentPath.value = null;
  pathsWithExperiments.value = [];
  cloneEngagementCategory.value = props.item?.engagement_category || null;
  if (props.isSuperAdmin) companiesLoading.value = true;
  await Promise.all([
    ...(props.isSuperAdmin
      ? [
          catchable({
            t: async () =>
              (companies.value = (await ApiCompanies.getCompanies()).map(el => ({
                title: el.name,
                value: el.id,
              }))),
            f: () => (companiesLoading.value = false),
            loadable: true,
          }),
        ]
      : []),
    getApps(props.currApp.company_id),
  ]);
  if (props.isSuperAdmin)
    cloneCompanyTarget.value = companies.value.find(el => el.value === props.currApp.company_id);
  showInProgress = false;
  if (!props.item?.experiment) loadPathsWithExperiments();
};

const close = () => modalRef.value?.close();

watch(
  () => cloneCompanyTarget.value?.value,
  companyId => {
    if (companyId !== lastFetchedCompanyId) getApps(companyId);
  },
);
watch(cloneIntoCurrApp, isSameApp => {
  if (!isSameApp) {
    cloneAsVariation.value = false;
    targetExperimentPath.value = null;
    pathsWithExperiments.value = [];
  } else if (!showInProgress && !props.item?.experiment) {
    loadPathsWithExperiments();
  }
});

onMounted(() => {
  catchable({
    // metadata-only payload (sequence_list_serializer flag) — the picker reads
    // id/name/type/dates + paths[0].device_type; without the flag the full
    // payload is served and carries the same fields.
    t: async () =>
      (sequences.value = await ApiSequences.getSequences(props.currApp.id, { serialize: "metadata" })),
    loadable: true,
  }).then(() => {
    cloneItemSequence.value =
      filteredSequences.value.find(({ value }) => value === props.item.sequence_id) ||
      filteredSequences.value[0];
  });
  catchable({
    t: async () => (pathGroups.value = await ApiPathGroups.getPathGroups(props.currApp.id)),
    loadable: true,
  }).then(() => (cloneItemZone.value = filteredPathGroupItems.value[0]));
});

defineExpose({ show, close });
</script>
